import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import {
  GENERATION_LEVELS,
  findLevelPriceCents,
  formatCreditPriceCents,
  normalizeGenerationLevel,
  pickDefaultGenerationLevel,
  saveGenerationLevel,
} from '../lib/generationLevel.js';
import '../styles/generation-level-picker.css';

// Module-scope cache for GET /api/credits/prices — same "fetch once per SPA
// session, share across every mounted picker" pattern as usePlanCatalog.js's
// _catalogPromise, so opening the picker on both CreatePost and EditPost in
// one session doesn't refetch every time (the gateway already caches this
// response for 60s per isolate too — this is the client-side half).
let _pricesPromise = null;

function loadPrices() {
  if (!_pricesPromise) {
    _pricesPromise = api.credits.getPrices()
      .then((res) => (res?.data && typeof res.data === 'object' ? res.data : res) || {})
      .catch(() => ({}));
  }
  return _pricesPromise;
}

/**
 * TASK-236 (#354, EPIC-023 pricing v2) — Economy / Standard / Premium picker
 * shown on text and image generation forms (CreatePost.jsx, EditPost.jsx
 * regenerate-text/regenerate-image). `operation` selects which price column
 * to show ('text' | 'image', matching ai-router's credit-prices.ts keys).
 *
 * Persists the choice per-user in localStorage (lib/generationLevel.js) so a
 * user who always wants Premium doesn't have to re-pick it on every post —
 * `userId` should be the current account id (falls back to 'anon' if
 * omitted, which just means the preference isn't kept across accounts on a
 * shared browser).
 *
 * Controlled: `value` + `onChange` — the parent owns the selected level and
 * is responsible for sending it as `level` in the generation request payload.
 */
export function GenerationLevelPicker({ value, onChange, operation = 'text', userId, disabled = false, compact = false }) {
  const { t } = useTranslation();
  const [prices, setPrices] = useState(null); // null = loading; {} = loaded (possibly empty)

  useEffect(() => {
    let cancelled = false;
    loadPrices().then((data) => {
      if (!cancelled) setPrices(data);
    });
    return () => { cancelled = true; };
  }, []);

  const currency = prices?.currency ? String(prices.currency).toUpperCase() : 'EUR';

  function handlePick(level) {
    if (disabled || level === value) return;
    onChange?.(level);
    saveGenerationLevel(userId, level, typeof window !== 'undefined' ? window.localStorage : null);
  }

  return (
    <div className={`glp ${compact ? 'glp-compact' : ''}`} role="radiogroup" aria-label={t('generationLevelPicker.ariaLabel')}>
      <div className="glp-head">
        <span className="glp-label">{t('generationLevelPicker.label')}</span>
        <span className="glp-info" tabIndex={0} role="note" aria-label={t('generationLevelPicker.whatIsThis')}>
          <Icon name="info" size={12} />
          <span className="glp-tooltip">{t('generationLevelPicker.tooltip')}</span>
        </span>
      </div>
      <div className="glp-options">
        {GENERATION_LEVELS.map((level) => {
          const priceCents = prices ? findLevelPriceCents(prices.prices, operation, level) : null;
          const priceFormatted = formatCreditPriceCents(priceCents, currency);
          const selected = normalizeGenerationLevel(value) === level;
          return (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`glp-opt ${selected ? 'is-active' : ''}`}
              onClick={() => handlePick(level)}
              disabled={disabled}
            >
              <span className="glp-opt-name">{t(`generationLevelPicker.levels.${level}`)}</span>
              <span className="glp-opt-price">
                {priceFormatted ? t('generationLevelPicker.priceEach', { price: priceFormatted }) : t('generationLevelPicker.included')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Convenience hook for a generation form: resolves the initial level from
 * the user's stored preference (or the product default) and returns
 * `[level, setLevel]` — `setLevel` both updates local state and persists the
 * choice, mirroring useState's contract so it's a drop-in initializer.
 */
export function useGenerationLevel() {
  const { user } = useAuth() || {};
  const userId = user?.id;
  const [level, setLevelState] = useState(() => pickDefaultGenerationLevel(userId, typeof window !== 'undefined' ? window.localStorage : null));

  function setLevel(next) {
    const normalized = normalizeGenerationLevel(next);
    setLevelState(normalized);
    saveGenerationLevel(userId, normalized, typeof window !== 'undefined' ? window.localStorage : null);
  }

  return [level, setLevel, userId];
}

export default GenerationLevelPicker;
